import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MarketPlaceCards.module.css';

// Constants
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const AUTH_TOKEN_KEY = 'authToken';

// API Service
const fetchAmazonStatus = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Amazon status: ${response.status}`);
  }

  return response.json();
};

const initiateAmazonLogin = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/login`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Login failed: ${response.status}`);
  }

  return response.json();
};

const finalizeAmazon = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/finalize`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Finalize failed: ${response.status}`);
  }

  return response.json();
};

const disconnectAmazon = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/disconnect`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Disconnect failed: ${response.status}`);
  }

  return response.json();
};

// Utility functions
const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY) ||
         localStorage.getItem('idToken') ||
         localStorage.getItem('refreshToken') ||
         localStorage.getItem('token') ||
         sessionStorage.getItem('token');
};


// Components
const AmazonLogo = () => (
  <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="#0e0e0eff" id="amazon">
    <path d="M5.75 27.18q.13-.21.42 0a27.73 27.73 0 0 0 14.14 3.77A27.86 27.86 0 0 0 30.37 29l.38-.15.35-.16a.46.46 0 0 1 .61.16c.15.2.1.39-.14.57s-.71.48-1.2.78a19.37 19.37 0 0 1-5 2.05 20.94 20.94 0 0 1-19.51-4.66.33.33 0 0 1-.16-.26.3.3 0 0 1 .05-.15Zm7.83-7.41a5.24 5.24 0 0 1 .88-3.07 5.53 5.53 0 0 1 2.42-1.93 13.36 13.36 0 0 1 3.46-.85c.47-.06 1.23-.13 2.29-.21v-.44a4.66 4.66 0 0 0-.37-2.27 1.93 1.93 0 0 0-1.71-.78h-.21a2.75 2.75 0 0 0-1.48.55 2.09 2.09 0 0 0-.81 1.3.65.65 0 0 1-.52.59l-3-.36c-.29-.07-.44-.23-.44-.47a1.41 1.41 0 0 1 0-.18 5 5 0 0 1 2.21-3.4 8.13 8.13 0 0 1 4-1.25h.7a6.6 6.6 0 0 1 4.63 1.53 6.44 6.44 0 0 1 .48.56c.14.2.26.38.35.54a3 3 0 0 1 .23.67c.07.3.12.5.16.61a4.53 4.53 0 0 1 .08.73v6.96a3.69 3.69 0 0 0 .2 1.23 3.5 3.5 0 0 0 .37.81c.13.16.32.43.6.79a.74.74 0 0 1 .16.42.45.45 0 0 1-.21.36l-2.34 2a.73.73 0 0 1-.75.05 7.69 7.69 0 0 1-.64-.6l-.39-.41c-.08-.09-.2-.26-.38-.51l-.36-.51a6.4 6.4 0 0 1-2.86 2 8 8 0 0 1-2.19.26 4.55 4.55 0 0 1-3.29-1.24 4.64 4.64 0 0 1-1.27-3.48Zm4.47-.52a2.46 2.46 0 0 0 .51 1.62 1.67 1.67 0 0 0 1.36.61H20.34A2.65 2.65 0 0 0 22 20a4.28 4.28 0 0 0 .46-1 5.47 5.47 0 0 0 .15-.95V16.2a9.38 9.38 0 0 0-2.29.21 2.79 2.79 0 0 0-2.27 2.84ZM29 27.62a.73.73 0 0 1 .16-.21 4.38 4.38 0 0 1 1.24-.6 8.26 8.26 0 0 1 1.93-.28 1.59 1.59 0 0 1 .49 0 2.39 2.39 0 0 1 1.41.39.86.86 0 0 1 .1.47v.18a5.94 5.94 0 0 1-.49 2.15 5.08 5.08 0 0 1-1.38 2 .4.4 0 0 1-.24.1.19.19 0 0 1-.1 0c-.1-.05-.13-.14-.08-.28a10 10 0 0 0 1-3.15.63.63 0 0 0-.1-.41c-.17-.21-.66-.32-1.46-.32-.29 0-.64 0-1 .06l-1.19.15a.32.32 0 0 1-.29-.07.1.1 0 0 1 0-.1.16.16 0 0 1 0-.08Z"></path>
  </svg>
);


const ConnectionDetails = ({ amazonData }) => {
  if (!amazonData || (!amazonData.seller_id && !amazonData.store_name && !amazonData.marketplace_id)) {
    return null;
  }

  return (
    <div className={styles.connectionDetails}>
      {amazonData.store_name && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Store:</span>
          <span className={styles.detailValue}>{amazonData.store_name}</span>
        </div>
      )}
      {amazonData.seller_id && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Seller ID:</span>
          <span className={styles.detailValue}>{amazonData.seller_id}</span>
        </div>
      )}
      {amazonData.marketplace_id && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Marketplace:</span>
          <span className={styles.detailValue}>{amazonData.marketplace_id}</span>
        </div>
      )}
    </div>
  );
};

const AmazonCard = ({ amazonData, amazonConnected, isLoading, isConnecting, onConnect, onDisconnect }) => {
  const hasConnectedBefore = amazonData?.seller_id;
  const buttonText = isLoading ? 'Loading...' : 
    isConnecting ? 'Connecting...' :
    hasConnectedBefore ? (amazonConnected ? 'Disconnect' : 'Reconnect') : 'Connect';
  const buttonAction = hasConnectedBefore ? 
    (amazonConnected ? onDisconnect : onConnect) : onConnect;

  return (
    <div className={`${styles.card} ${styles.amazonCard}`}>
      {hasConnectedBefore && (
        <div className={styles.connectedBadge}>
          {amazonConnected ? 'Connected' : 'Token Expired'}
        </div>
      )}
      
      <div>
        <div className={`${styles.logoContainer} ${styles.amazonLogo}`}>
          <AmazonLogo />
        </div>
        <h3 className={styles.title}>Amazon</h3>
        <ConnectionDetails amazonData={amazonData} />
      </div>
      
      <button 
        className={styles.button}
        onClick={buttonAction}
        disabled={isLoading || isConnecting}
        aria-label={`${buttonText} Amazon account`}
      >
        {buttonText}
      </button>
    </div>
  );
};

const DisabledCard = ({ title, logo, logoStyle, cardStyle }) => (
  <div className={`${styles.card} ${cardStyle} ${styles.cardDisabled}`}>
    <div className={styles.disabledOverlay}></div>
    <div>
      <div className={`${styles.logoContainer} ${logoStyle}`}>
        {logo}
      </div>
      <h3 className={styles.title}>{title}</h3>
    </div>
    <button 
      className={`${styles.button} ${styles.buttonDisabled}`}
      disabled
    >
      Coming Soon
    </button>
  </div>
);

const MarketPlaceCards = () => {
  const queryClient = useQueryClient();
  // State for Amazon connection status
  const [amazonConnected, setAmazonConnected] = useState(false);
  const [amazonData, setAmazonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);


  // Function to check Amazon data and connection status
  const checkAmazonStatus = useCallback(async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        setIsLoading(false);
        return;
      }

      const data = await fetchAmazonStatus(token);

      // Check if user has connected before (has seller_id)
      const hasConnectedBefore = !!data.seller_id;

      if (data.connected) {
        setAmazonConnected(true);
        setAmazonData(data);

        // If store_name or marketplace_id is still "Pending", call finalize
        if (data.store_name === "Pending" || data.marketplace_id === "Pending") {
          setIsConnecting(true);
          try {
            const finalizeData = await finalizeAmazon(token);
            // Update display with finalized data
            setAmazonData({
              connected: true,
              store_name: finalizeData.store_name,
              seller_id: finalizeData.seller_id,
              marketplace_id: finalizeData.marketplace_id,
            });
            // Invalidate catalogue cache so new data is available
            queryClient.invalidateQueries({ queryKey: ['catalogue-health-data'] });
          } catch (finalizeError) {
            console.error("Failed to finalize Amazon connection:", finalizeError);
          } finally {
            setIsConnecting(false);
          }
        }
      } else if (hasConnectedBefore) {
        setAmazonConnected(false);
        setAmazonData(data);
      } else {
        setAmazonConnected(false);
        setAmazonData(null);
      }
    } catch (error) {
      setAmazonConnected(false);
      setAmazonData(null);
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  // Check Amazon connection status on component mount and when component becomes visible
  useEffect(() => {
    // Reset connecting state when component mounts
    setIsConnecting(false);

    // Clean up amazon_connected query param from OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.has('amazon_connected')) {
      params.delete('amazon_connected');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    checkAmazonStatus();
    
    // Also check status when the page becomes visible (user returns from OAuth)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setIsConnecting(false); // Reset connecting state when user returns
        checkAmazonStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check when the page gains focus (user returns from OAuth)
    const handleFocus = () => {
      setIsConnecting(false); // Reset connecting state when page gains focus
      checkAmazonStatus();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkAmazonStatus]);

  // Function to handle Amazon connection
  const handleAmazonConnect = useCallback(async (event) => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        return;
      }

      // Set connecting state
      setIsConnecting(true);

      try {
        const data = await initiateAmazonLogin(token);
        
        // Redirect to Amazon's authorization page
        if (data.auth_url) {
          // Keep connecting state active until redirect happens
          window.location.href = data.auth_url;
          return; // Exit early - don't reset state
        } else {
          setIsConnecting(false);
        }
      } catch (apiError) {
        setIsConnecting(false);
      }
    } catch (error) {
      setIsConnecting(false);
    }
  }, []);

  // Function to handle Amazon disconnection
  const handleAmazonDisconnect = useCallback(async (event) => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        return;
      }

      // Show loading state
      const button = event.target;
      const originalText = button.textContent;
      button.textContent = 'Disconnecting...';
      button.disabled = true;

      try {
        await disconnectAmazon(token);
        queryClient.invalidateQueries({ queryKey: ['catalogue-health-data'] });
        setAmazonConnected(false);
        setAmazonData(null);
      } catch (apiError) {
      }
    } catch (error) {
    } finally {
      // Reset button state
      const button = event.target;
      button.textContent = 'Disconnect';
      button.disabled = false;
    }
  }, []);

  return (
    <div className={styles.container}>
      <AmazonCard 
        amazonData={amazonData}
        amazonConnected={amazonConnected}
        isLoading={isLoading}
        isConnecting={isConnecting}
        onConnect={handleAmazonConnect}
        onDisconnect={handleAmazonDisconnect}
      />
    </div>
  );
};

export default MarketPlaceCards;